import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Row, Col, Button, Table } from 'react-bootstrap'
import Message from '../../shared_components/Message'
import ToastMessage from '../components/ToastMessage'
import Loader from '../../shared_components/Loader'

import {
  getJitaiRespList,
  deleteJitaiResp,
} from '../actions/jitaiActions'
import {
  JITAI_RESP_DELETE_RESET,
  JITAI_RESP_LIST_RESET,
  JITAI_RESP_CREATE_RESET,
  JITAI_RESP_UPDATE_RESET,
} from '../constants/jitaiConstants'

import resetErrorState from '../utils/resetErrorState'

import hasValidLoginCredential from '../../admin_module/utils/hasValidLoginCredential'
import { getAllAccess } from '../../access_module/actions/accessActions'
import { GET_ALL_ACCESS_RESET } from '../../access_module/constants/accessConstants'
import { listStudyDetails } from '../../study_module/actions/studyActions'
import { STUDY_DETAILS_RESET } from '../../study_module/constants/studyConstants'
import { getQxList } from '../../qx_module/actions/qxActions'
import { QX_LIST_RESET } from '../../qx_module/constants/qxConstants'
import UpsertJitaiResp from '../components/UpsertJitaiResp'

const JitaiRespScreen = ({ history, match }) => {
  const studyId = match.params.studyId

  // For toast message
  const [toastMessages, setToastMessages] = useState([])

  const toastOnCloseHandler = async (uid) => {
    setToastMessages((toastMessages) =>
      toastMessages.filter((message) => message.uid !== uid)
    )
  }

  const dispatch = useDispatch()

  //It is needed to check if the logged in user is an admin
  const userLogin = useSelector((state) => state.userLogin)
  const { userInfo, logout } = userLogin

  const jitaiRespList = useSelector((state) => state.jitaiRespList)
  const {
    jitaiResps,
    error: errorGetList,
    loading: loadingGetList,
    success: successGetList,
  } = jitaiRespList

  const jitaiRespDelete = useSelector((state) => state.jitaiRespDelete)
  const {
    loading: loadingDelete,
    error: errorDelete,
    success: successDelete,
  } = jitaiRespDelete

  const jitaiRespCreate = useSelector((state) => state.jitaiRespCreate)
  const { success: successCreate } = jitaiRespCreate

  const jitaiRespUpdate = useSelector((state) => state.jitaiRespUpdate)
  const { success: successUpdate } = jitaiRespUpdate

  const allAccess = useSelector((state) => state.allAccess)
  const {
    loading: loadingGetAllAccess,
    error: errorGetAllAccess,
    success: successGetAllAccess,
    allAccessList,
  } = allAccess

  const studyDetails = useSelector((state) => state.studyDetails)
  const {
    loading: loadingStudy,
    error: errorStudy,
    study: loadedStudy,
    success: successStudy,
  } = studyDetails

  const qxListState = useSelector((state) => state.qxList)
  const {
    loading: loadingQx,
    error: errorQx,
    qx_list: qxList,
    success: successQx,
  } = qxListState

  useEffect(() => {
    if (!hasValidLoginCredential(userInfo, logout, window)) {
      dispatch({ type: JITAI_RESP_LIST_RESET })
      history.push('/login')
    } else {
      if (!loadingGetList && !successGetList && !errorGetList) {
        resetErrorState(dispatch)
        // Put the reset for getting all access just before loading the main content again
        dispatch({ type: GET_ALL_ACCESS_RESET })
        dispatch(getJitaiRespList(studyId))
      }

      if (!loadingGetAllAccess && !successGetAllAccess && !errorGetAllAccess) {
        dispatch(getAllAccess())
      }

      if (!loadingStudy && !errorStudy && !successStudy) {
        dispatch(listStudyDetails(studyId))
      }

      if (!loadingQx && !errorQx && !successQx) {
        dispatch(getQxList())
      }

      if (successDelete) {
        dispatch({ type: JITAI_RESP_DELETE_RESET })
        dispatch(getJitaiRespList(studyId))
      }

      if (successCreate) {
        dispatch(getJitaiRespList(studyId))
      }

      if (successUpdate) {
        dispatch(getJitaiRespList(studyId))
      }
    }
  }, [
    dispatch,
    history,
    userInfo,
    studyId,
    logout,
    loadingGetList,
    loadingGetAllAccess,
    loadingStudy,
    loadingQx,
    errorGetList,
    errorGetAllAccess,
    errorStudy,
    errorQx,
    successDelete,
    successCreate,
    successUpdate,
    successGetList,
    successGetAllAccess,
    successStudy,
    successQx,
  ])

  const deleteJitaiRespHandler = (jitaiResp) => {
    if (jitaiResp.implemented) {
      window.alert('Deletion is not allowed for implemented response. Contact administrator for further action.')
      return
    }
    if (
      window.confirm('Are you sure you want to delete this jitai response?')
    ) {
      resetErrorState(dispatch)
      dispatch(deleteJitaiResp(studyId, jitaiResp._id))
    }
  }

  const headingStyle = {
    fontSize: '2rem',
    fontWeight: 'bold',
  }

  return (
    <>
      {/* Display toast messages */}
      <div
        style={{
          position: 'absolute',
          backgroundColor: 'transparent',
          pointerEvents: 'none',
          height: '80vh',
          width: '80vw',
          zIndex: 1,
        }}
      >
        {toastMessages.map((toastMessage) => (
          <ToastMessage
            key={toastMessage.uid}
            variant={toastMessage.variant}
            onClose={() => toastOnCloseHandler(toastMessage.uid)}
          >
            {toastMessage.message}
          </ToastMessage>
        ))}
      </div>

      <Row className='align-items-center'>
        <Col>
          <h1 style={headingStyle}>Jitai Responses</h1>
          {loadedStudy && (
            <p className='mb-3'>
              <strong>Study:</strong> {loadedStudy.chin_name} (
              {loadedStudy.study_code})
            </p>
          )}
        </Col>
        <UpsertJitaiResp
          studyId={studyId}
          jitaiRespList={jitaiResps}
          updatedJitaiResp={undefined}
          qxList={qxList}
        ></UpsertJitaiResp>
      </Row>

      {errorDelete && <Message variant='danger'>{errorDelete}</Message>}
      {loadingGetList || loadingDelete || loadingQx ? (
        <Loader />
      ) : errorGetList ? (
        <Message variant='danger'>{errorGetList}</Message>
      ) : (
        <>
          <p>
            Manage jitai response definitions for this study. These define what
            types of responses should be collected after a participant accepts a
            JITAI task.
          </p>
          <p>
            <strong>Response Types:</strong>
          </p>
          <ul>
            <li><strong>qx (Questionnaire):</strong> Response is collected via a questionnaire</li>
            <li><strong>custom:</strong> Response is collected through a custom implementation (developer-defined)</li>
          </ul>
          <Table striped bordered hover responsive className='table-sm'>
            <thead>
              <tr>
                <th>Code</th>
                <th>Type</th>
                <th>Questionnaire / Notes</th>
                <th>Implemented</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {!logout &&
                jitaiResps && jitaiResps.map((jitaiResp) => (
                  <tr key={jitaiResp._id}>
                    <td className='align-middle'>{jitaiResp.code}</td>
                    <td className='align-middle'>
                      <span className={jitaiResp.resp_type === 'qx' ? 'text-primary' : 'text-info'}>
                        {jitaiResp.resp_type === 'qx' ? 'Questionnaire' : 'Custom'}
                      </span>
                    </td>
                    <td className='align-middle'>
                      {jitaiResp.resp_type === 'qx' && jitaiResp.questionnaire ? (
                        <span>
                          {jitaiResp.questionnaire.chin_name} ({jitaiResp.questionnaire.abbrev})
                        </span>
                      ) : jitaiResp.resp_type === 'custom' && jitaiResp.custom_implementation_notes ? (
                        <span style={{ 
                          maxWidth: '300px', 
                          display: 'inline-block', 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {jitaiResp.custom_implementation_notes}
                        </span>
                      ) : (
                        <span className='text-muted'>—</span>
                      )}
                    </td>
                    <td className='align-middle'>
                      {jitaiResp.resp_type === 'qx' ? (
                        <span className='text-muted'>—</span>
                      ) : jitaiResp.implemented ? (
                        <span className='text-success'>
                          <i className='fas fa-check'></i> Yes
                        </span>
                      ) : (
                        <span className='text-warning'>
                          <i className='fas fa-times'></i> No
                        </span>
                      )}
                    </td>
                    <td className='align-middle'>
                      <UpsertJitaiResp
                        studyId={studyId}
                        jitaiRespList={jitaiResps}
                        updatedJitaiResp={jitaiResp}
                        qxList={qxList}
                      ></UpsertJitaiResp>

                      <Button
                        variant='danger'
                        className='btn-sm mx-1'
                        onClick={() =>
                          deleteJitaiRespHandler(jitaiResp)
                        }
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </>
      )}
    </>
  )
}

export default JitaiRespScreen

